// Super-admin Threat Insights catalog manager.
//
// Two-mode page:
//   - Catalog mode (default): browse all rules with search + filters,
//     toggle global enable/disable, open a drawer per rule that shows
//     the SQL query template + remediation + per-org overrides.
//   - Per-org mode (?org=ID): same table, but each row reflects the
//     org-specific effective config (override merged with global).
//     Toggling a row writes a per-org override.
//
// Auth: SUPER_ADMIN only. The backend enforces this; we hide the page
// from the side menu for non-super-admins.

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import {
  Box, Stack, Typography, TextField, Select, MenuItem, FormControl,
  InputLabel, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Tooltip, Switch, Drawer,
  Button, Divider, CircularProgress, Snackbar, Alert,
} from "@mui/material";
import { Search as SearchIcon, Close as CloseIcon, Refresh as RefreshIcon } from "@mui/icons-material";

import useAuth from "../../../../hooks/useAuth";
import threatInsightAdmin from "../../../../utils/axios/v1/threatInsightAdminAxios";
import PageHeader from "../../../../components/pages/_new/super/common/PageHeader";

const SEVERITY_COLORS = {
  low: "#999999",
  medium: "#FFB020",
  high: "#FF6B35",
  critical: "#D32F2F",
};

function SeverityChip({ value, size = "small" }) {
  return (
    <Chip
      label={value}
      size={size}
      sx={{
        bgcolor: (SEVERITY_COLORS[value] || "#999") + "20",
        color: SEVERITY_COLORS[value] || "#999",
        fontWeight: 600,
        textTransform: "capitalize",
      }}
    />
  );
}

function RuleDrawer({ rule, override, orgId, onClose, onToggleGlobal, onSaveOverride, onClearOverride }) {
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (rule) {
      setDraft({
        enabled: override?.enabled ?? null,
        severity: override?.severity ?? null,
        window_minutes: override?.window_minutes ?? null,
        threshold: override?.threshold ?? null,
        cooldown_minutes: override?.cooldown_minutes ?? null,
        notes: override?.notes ?? "",
      });
    }
  }, [rule, override]);

  if (!rule) return null;

  const effective = {
    enabled: orgId
      ? (override?.enabled !== null && override?.enabled !== undefined
          ? override.enabled : rule.enabled)
      : rule.enabled,
    severity: override?.severity || rule.severity,
    window_minutes: override?.window_minutes || rule.window_minutes,
    threshold: override?.threshold || rule.threshold,
    cooldown_minutes: override?.cooldown_minutes ?? rule.cooldown_minutes,
  };

  return (
    <Drawer anchor="right" open={!!rule} onClose={onClose} PaperProps={{ sx: { width: 700 } }}>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{rule.id}</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
        <Typography variant="subtitle1" sx={{ mt: 1 }}>{rule.name}</Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }}>
          <SeverityChip value={effective.severity} />
          <Chip label={rule.category} size="small" />
          {rule.taxonomy_tag && <Chip label={rule.taxonomy_tag} size="small" variant="outlined" />}
          {(rule.source_streams || []).map((s) => (
            <Chip key={s} label={s} size="small" variant="outlined" />
          ))}
        </Stack>

        <Typography variant="body2" color="text.secondary">{rule.description}</Typography>

        {rule.notes && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: "#FFF8E1", border: "1px solid #FFE082", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>NOTES</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{rule.notes}</Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Per-org override section if org is selected */}
        {orgId ? (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Per-organisation override
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              Leave a field empty to inherit the global rule value. The scanner skips evaluation entirely for sites in this org when the rule is disabled here.
            </Typography>

            <Stack spacing={2}>
              <FormControl size="small">
                <InputLabel>Enabled</InputLabel>
                <Select
                  label="Enabled"
                  value={draft.enabled === null ? "inherit" : String(draft.enabled)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDraft({ ...draft, enabled: v === "inherit" ? null : v === "true" });
                  }}
                >
                  <MenuItem value="inherit">Inherit (global: {rule.enabled ? "on" : "off"})</MenuItem>
                  <MenuItem value="true">Force enable</MenuItem>
                  <MenuItem value="false">Force disable</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small">
                <InputLabel>Severity</InputLabel>
                <Select
                  label="Severity"
                  value={draft.severity || "inherit"}
                  onChange={(e) => setDraft({ ...draft, severity: e.target.value === "inherit" ? null : e.target.value })}
                >
                  <MenuItem value="inherit">Inherit (global: {rule.severity})</MenuItem>
                  {["low", "medium", "high", "critical"].map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                label="Window (minutes)"
                placeholder={`Inherit (${rule.window_minutes})`}
                type="number"
                value={draft.window_minutes ?? ""}
                onChange={(e) => setDraft({ ...draft, window_minutes: e.target.value ? parseInt(e.target.value, 10) : null })}
                inputProps={{ min: 1, max: 1440 }}
              />
              <TextField
                size="small"
                label="Threshold"
                placeholder={`Inherit (${rule.threshold})`}
                type="number"
                value={draft.threshold ?? ""}
                onChange={(e) => setDraft({ ...draft, threshold: e.target.value ? parseInt(e.target.value, 10) : null })}
                inputProps={{ min: 1 }}
              />
              <TextField
                size="small"
                label="Cooldown (minutes)"
                placeholder={`Inherit (${rule.cooldown_minutes})`}
                type="number"
                value={draft.cooldown_minutes ?? ""}
                onChange={(e) => setDraft({ ...draft, cooldown_minutes: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                inputProps={{ min: 0 }}
              />
              <TextField
                size="small"
                label="Notes"
                placeholder="Reason for override (optional, max 500 chars)"
                multiline
                rows={2}
                value={draft.notes || ""}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />

              <Stack direction="row" spacing={1}>
                <Button variant="contained" size="small" onClick={() => onSaveOverride(rule.id, draft)}>
                  Save override
                </Button>
                {override && (
                  <Button variant="outlined" size="small" color="warning" onClick={() => onClearOverride(rule.id)}>
                    Clear override
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Global config</Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Switch
                checked={rule.enabled}
                onChange={(_, v) => onToggleGlobal(rule.id, v)}
              />
              <Typography variant="body2">Enabled globally</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Window: {rule.window_minutes}min · Threshold: {rule.threshold} · Cooldown: {rule.cooldown_minutes}min
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Query template</Typography>
        <Box sx={{ p: 1.5, bgcolor: "#FAFAFA", border: "1px solid #E0E0E0", borderRadius: 1, fontFamily: "JetBrains Mono, monospace", fontSize: 11, overflow: "auto", maxHeight: 250 }}>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{rule.query_template}</pre>
        </Box>

        {rule.remediation && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Remediation</Typography>
            <Typography variant="body2">{rule.remediation}</Typography>
          </Box>
        )}

        {rule.references_url && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Reference</Typography>
            <a href={rule.references_url} target="_blank" rel="noreferrer">{rule.references_url}</a>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

export default function T_ThreatInsightRules() {
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const orgId = searchParams.get("org") || "";

  const [rules, setRules] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({
    categories: [],
    // TI-8: enriched category rows with { name, total_rules, enabled_rules, is_coming_soon }.
    categories_status: [],
    severities: [],
    taxonomies: [],
    source_streams: [],
  });
  const [orgs, setOrgs] = useState([]);
  const [overrides, setOverrides] = useState({});  // ruleId → override
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(null);
  const [snack, setSnack] = useState(null);

  // Filters
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [severity, setSeverity] = useState(searchParams.get("severity") || "");
  const [enabledFilter, setEnabledFilter] = useState(searchParams.get("enabled") || "");
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "0", 10));
  const limit = 100;

  const refresh = async () => {
    setLoading(true);
    // The rules list is the page's primary content — fetch it
    // independently so a failure in the orgs/facets/overrides side-
    // calls doesn't blank the table. Each side-call gets its own
    // catch and surfaces a non-blocking warning.
    const params = { page, limit };
    if (q) params.q = q;
    if (category) params.category = category;
    if (severity) params.severity = severity;
    if (enabledFilter) params.enabled = enabledFilter;
    try {
      const rulesRes = await threatInsightAdmin.searchRules(params);
      setRules(rulesRes.data.items);
      setTotal(rulesRes.data.total);
    } catch (e) {
      setSnack({ severity: "error", message: e?.response?.data?.message || e.message });
      setRules([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    // Fire-and-forget the secondary calls. Each failure is silent in
    // the UI (we just keep the prior state).
    threatInsightAdmin.getFacets()
      .then((r) => setFacets(r.data))
      .catch(() => { /* keep prior facets */ });
    threatInsightAdmin.listOrganisations()
      .then((r) => setOrgs(r.data.items))
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("Threat Insights: orgs list failed", e?.response?.data?.message || e.message);
        setOrgs([]);
      });
    if (orgId) {
      threatInsightAdmin.listOverrides(orgId)
        .then((r) => {
          const m = {};
          for (const o of r.data.items) m[o.rule_id] = o;
          setOverrides(m);
        })
        .catch(() => setOverrides({}));
    } else {
      setOverrides({});
    }
  };

  useEffect(() => { if (isAuthenticated) refresh(); /* eslint-disable-next-line */ }, [isAuthenticated, q, category, severity, enabledFilter, page, orgId]);

  // Sync URL
  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (category) next.category = category;
    if (severity) next.severity = severity;
    if (enabledFilter) next.enabled = enabledFilter;
    if (page) next.page = String(page);
    if (orgId) next.org = orgId;
    setSearchParams(next, { replace: true });
    /* eslint-disable-next-line */
  }, [q, category, severity, enabledFilter, page, orgId]);

  const toggleGlobal = async (id, enabled) => {
    try {
      await threatInsightAdmin.updateRule(id, { enabled });
      setSnack({ severity: "success", message: `${id} ${enabled ? "enabled" : "disabled"} globally` });
      refresh();
    } catch (e) {
      setSnack({ severity: "error", message: e?.response?.data?.message || e.message });
    }
  };

  const togglePerOrg = async (id, currentEffective) => {
    if (!orgId) return;
    try {
      const newOverride = { enabled: !currentEffective };
      await threatInsightAdmin.upsertOverride(orgId, id, newOverride);
      setSnack({ severity: "success", message: `${id} ${!currentEffective ? "enabled" : "disabled"} for org` });
      refresh();
    } catch (e) {
      setSnack({ severity: "error", message: e?.response?.data?.message || e.message });
    }
  };

  const saveOverride = async (id, patch) => {
    try {
      // Drop fields the user left blank (null) so the backend stores
      // explicit override values only.
      const body = {};
      for (const k of ["enabled", "severity", "window_minutes", "threshold", "cooldown_minutes"]) {
        if (patch[k] !== null && patch[k] !== undefined) body[k] = patch[k];
      }
      if (patch.notes) body.notes = patch.notes;
      if (!Object.keys(body).length) {
        setSnack({ severity: "warning", message: "Nothing to save — set at least one field" });
        return;
      }
      await threatInsightAdmin.upsertOverride(orgId, id, body);
      setSnack({ severity: "success", message: `Override saved for ${id}` });
      setOpenDrawer(null);
      refresh();
    } catch (e) {
      setSnack({ severity: "error", message: e?.response?.data?.message || e.message });
    }
  };

  const clearOverride = async (id) => {
    try {
      await threatInsightAdmin.deleteOverride(orgId, id);
      setSnack({ severity: "success", message: `Override cleared for ${id}` });
      setOpenDrawer(null);
      refresh();
    } catch (e) {
      setSnack({ severity: "error", message: e?.response?.data?.message || e.message });
    }
  };

  const totalPages = Math.ceil(total / limit);
  const selectedOrg = orgs.find((o) => o.organisation_id === orgId);

  return (
    <>
      <Helmet title="Threat Insights — Super Admin" />
      <Box sx={{ p: 3 }}>
        <PageHeader title="Threat Insights Rules" />

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {total} total rules · {orgId ? `viewing org: ${selectedOrg?.name || orgId}` : "global view"}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={refresh} disabled={loading} size="small">
            <RefreshIcon />
          </IconButton>
        </Stack>

        {/* Filters */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search rule id / name / description"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
            sx={{ minWidth: 350 }}
          />
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={category}
              onChange={(e) => { setCategory(e.target.value); setPage(0); }}
              renderValue={(v) => v || "All"}
            >
              <MenuItem value="">All</MenuItem>
              {/* TI-8: split active vs coming-soon so nobody claims fake coverage. */}
              {(facets.categories_status && facets.categories_status.length > 0)
                ? (() => {
                    const active = facets.categories_status.filter((c) => !c.is_coming_soon);
                    const soon = facets.categories_status.filter((c) => c.is_coming_soon);
                    return [
                      <MenuItem
                        key="__header_active"
                        disabled
                        sx={{ opacity: 1, fontSize: 11, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: "0.06em" }}
                      >
                        Active
                      </MenuItem>,
                      ...active.map((c) => (
                        <MenuItem key={c.name} value={c.name}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                            <Box sx={{ flex: 1 }}>{c.name}</Box>
                            <Chip
                              label={`${c.enabled_rules}/${c.total_rules}`}
                              size="small"
                              sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: "#EDF3FE", color: "#0244BE" }}
                            />
                          </Stack>
                        </MenuItem>
                      )),
                      ...(soon.length > 0
                        ? [
                            <MenuItem
                              key="__header_soon"
                              disabled
                              sx={{ opacity: 1, fontSize: 11, fontWeight: 700, color: "#B25A1A", textTransform: "uppercase", letterSpacing: "0.06em", mt: 1 }}
                            >
                              Coming soon
                            </MenuItem>,
                            ...soon.map((c) => (
                              <MenuItem key={c.name} value={c.name} disabled sx={{ opacity: 0.55 }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                                  <Box sx={{ flex: 1, color: "#7B7B7B" }}>{c.name}</Box>
                                  <Chip
                                    label={`${c.total_rules} planned`}
                                    size="small"
                                    sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: "#FFF6D0", color: "#7a5b10" }}
                                  />
                                </Stack>
                              </MenuItem>
                            )),
                          ]
                        : []),
                    ];
                  })()
                : facets.categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Severity</InputLabel>
            <Select label="Severity" value={severity} onChange={(e) => { setSeverity(e.target.value); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              {facets.severities.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Enabled</InputLabel>
            <Select label="Enabled" value={enabledFilter} onChange={(e) => { setEnabledFilter(e.target.value); setPage(0); }}>
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="true">Enabled</MenuItem>
              <MenuItem value="false">Disabled</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Organisation</InputLabel>
            <Select
              label="Organisation"
              value={orgId}
              onChange={(e) => setSearchParams(e.target.value ? { ...Object.fromEntries(searchParams), org: e.target.value } : Object.fromEntries([...searchParams].filter(([k]) => k !== "org")), { replace: true })}
            >
              <MenuItem value="">Global view</MenuItem>
              {orgs.map((o) => (
                <MenuItem key={o.organisation_id} value={o.organisation_id}>
                  {o.name} {o.override_count > 0 ? `(${o.override_count} overrides)` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Rules table */}
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 110, fontWeight: 600 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ width: 130, fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ width: 110, fontWeight: 600 }}>Severity</TableCell>
                <TableCell sx={{ width: 110, fontWeight: 600 }}>Streams</TableCell>
                <TableCell sx={{ width: 110, fontWeight: 600, textAlign: "center" }}>
                  {orgId ? "Enabled (org)" : "Enabled"}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rules.length === 0 && (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: "center", p: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              )}
              {rules.map((rule) => {
                const override = overrides[rule.id];
                const effective = orgId
                  ? {
                      enabled: override?.enabled !== null && override?.enabled !== undefined ? override.enabled : rule.enabled,
                      severity: override?.severity || rule.severity,
                    }
                  : rule;
                const isOverridden = orgId && !!override;
                return (
                  <TableRow
                    key={rule.id}
                    hover
                    sx={{ cursor: "pointer", bgcolor: isOverridden ? "#FFFDE7" : "transparent" }}
                    onClick={() => setOpenDrawer(rule)}
                  >
                    <TableCell sx={{ fontFamily: "JetBrains Mono, monospace", fontSize: 12 }}>
                      {rule.id}
                      {isOverridden && (
                        <Tooltip title="Has per-org override"><Chip label="●" size="small" sx={{ ml: 0.5, bgcolor: "#FFC107", color: "#fff", height: 16, "& .MuiChip-label": { px: 0.5 } }} /></Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{rule.name}</TableCell>
                    <TableCell><Chip label={rule.category} size="small" /></TableCell>
                    <TableCell><SeverityChip value={effective.severity} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {(rule.source_streams || []).slice(0, 3).map((s) => (
                          <Chip key={s} label={s} size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                      <Switch
                        size="small"
                        checked={!!effective.enabled}
                        onChange={(_, v) => {
                          if (orgId) togglePerOrg(rule.id, !!effective.enabled);
                          else toggleGlobal(rule.id, v);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && rules.length === 0 && (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: "center", p: 4, color: "text.secondary" }}>No rules match filters.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
            <Button size="small" disabled={page <= 0} onClick={() => setPage(page - 1)}>Prev</Button>
            <Typography variant="body2" sx={{ alignSelf: "center" }}>Page {page + 1} of {totalPages}</Typography>
            <Button size="small" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next</Button>
          </Stack>
        )}
      </Box>

      <RuleDrawer
        rule={openDrawer}
        override={openDrawer ? overrides[openDrawer.id] : null}
        orgId={orgId}
        onClose={() => setOpenDrawer(null)}
        onToggleGlobal={toggleGlobal}
        onSaveOverride={saveOverride}
        onClearOverride={clearOverride}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snack && <Alert severity={snack.severity}>{snack.message}</Alert>}
      </Snackbar>
    </>
  );
}
